import { Button, Stack, TextField } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTranslation } from "react-i18next";
import { PageContainer } from "../../components/primitives/layout/PageContainer";
import { gaps } from "../../components/primitives/spacing";
import { BrandTitle } from "../../components/primitives/data-display/BrandTitle";
import { Subtitle } from "../../components/primitives/data-display/Subtitle";
import { Meta } from "../../components/primitives/data-display/Meta";
import { FinePrint } from "../../components/primitives/data-display/FinePrint";
import { Note } from "../../components/primitives/data-display/Note";
import { Pill } from "../../components/primitives/data-display/Pill";
import { RateLimitCaption } from "../../components/domain/feedback/RateLimitCaption";
import { useLanding, MAX_CLAIM_LENGTH } from "./useLanding";

/**
 * Entry point of the verification flow.
 * Accepts free text (claim) or a URL to a page to extract from.
 * Routes to /fetching for URLs, or directly to /review for text.
 */
export default function Landing() {
  const { input, setInput, handleSubmit, rateLimitState, minutesUntilReset } = useLanding();
  const isRateLimited = rateLimitState?.remaining === 0;
  const isOverCharLimit = input.length > MAX_CLAIM_LENGTH;
  const { t } = useTranslation();

  const trust = t("landing.trust", { returnObjects: true }) as string[];
  const examples = t("landing.examples", { returnObjects: true }) as string[];

  return (
    <>
      <PageContainer center sx={{ pt: { xs: 2, sm: 2 } }}>
        {/* Hero: wordmark, one-line pitch, and the credibility markers as a set. */}
        <Stack gap={gaps.snug} alignItems="center">
          <BrandTitle>{t("landing.title")}</BrandTitle>
          <Subtitle>{t("landing.subtitle")}</Subtitle>
        </Stack>

        {/* Action: the input is the hero object; examples sit immediately beneath it. */}
        <Stack gap={gaps.base} sx={{ width: "100%", maxWidth: 520 }}>
          <Stack component="form" onSubmit={handleSubmit} gap={gaps.base}>
            <TextField
              multiline
              minRows={3}
              maxRows={8}
              fullWidth
              placeholder={t("landing.placeholder")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              autoFocus
              disabled={isRateLimited}
              error={isOverCharLimit}
              helperText={t("landing.charLimit", {
                count: input.length,
                max: MAX_CLAIM_LENGTH,
              })}
              slotProps={{ htmlInput: { maxLength: MAX_CLAIM_LENGTH } }}
            />
            {rateLimitState !== null && minutesUntilReset !== null && (
              <RateLimitCaption
                remaining={rateLimitState.remaining}
                limit={rateLimitState.limit}
                minutesUntilReset={minutesUntilReset}
              />
            )}
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={!input.trim() || isRateLimited || isOverCharLimit}
              endIcon={<ArrowForwardIcon />}
            >
              {t("landing.cta")}
            </Button>
          </Stack>

          <Stack gap={gaps.tight} alignItems="center">
            <Meta>{t("landing.tryLabel")}</Meta>
            <Stack direction="row" gap={gaps.snug} justifyContent="center" flexWrap="wrap">
              {examples.map((example) => (
                <Pill
                  key={example}
                  tone="accent"
                  label={example}
                  onClick={() => setInput(example)}
                />
              ))}
            </Stack>
          </Stack>
        </Stack>

        {/* Attribution — quiet footer line. */}
        <Note color="text.secondary">{t("landing.poweredBy")}</Note>
        <FinePrint sx={{ maxWidth: 480, textAlign: "center" }}>{t("landing.disclaimer")}</FinePrint>
      </PageContainer>
    </>
  );
}
